import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ViewUser } from '../../modules/user-role/contract/view-user.contract';
import { WidgetDefinition } from '../../pages/dashboard/contract/widget-definition.class';
import { DimensionSystem } from '../../shared/enums/dimension-system.enum';
import { Languages } from '../../shared/enums/languages.enum';
import { UserLocalStorageService } from '../../shared/services/user-local-storage.service';
import { AutomatedTransfersOnTheRoadListConfiguration } from '../contract/user-configuration/automated-transfers-on-the-road-list-configuration.contract';
import { AutomatedTransfersScheduledListConfiguration } from '../contract/user-configuration/automated-transfers-scheduled-list-configuration.contract';
import { ContactListConfiguration } from '../contract/user-configuration/contact-list-configuration.contract';
import { DimensionUnitConfiguration } from '../contract/user-configuration/dimension-unit.configuration.contract';
import { EmployeeListConfiguration } from '../contract/user-configuration/employee-list-configuration.contract';
import { EquipmentEventsConfiguration } from '../contract/user-configuration/equipment-events-configuration.contract';
import { EquipmentListConfiguration } from '../contract/user-configuration/equipment-list-configuration.contract';
import { EquipmentSearchConfiguration } from '../contract/user-configuration/equipment-search-configuration.contract';
import { MaintenanceConfigListConfiguration } from '../contract/user-configuration/maintenance-config-list-configuration.contract';
import { MaintenanceListConfiguration } from '../contract/user-configuration/maintenance-list-configuration.contract';
import { MapConfiguration } from '../contract/user-configuration/map-configuration.contract';
import { TransferLogConfiguration } from '../contract/user-configuration/transfer-log-configuration.contract';
import { UserConfiguration } from '../contract/user-configuration/user-configuration.contract';
import { AuthRealmService } from './auth-realm.service';
import {
  AlarmTypeConfigListConfiguration
} from '../contract/user-configuration/alarm-settings-list-configuration.contract';

export class DocumentsSortConfiguration {
  constructor(public sortParameter: string) {}
}

@Injectable({
  providedIn: 'root',
})
export class UserConfigurationService {
  private userLocalStorageService = inject(UserLocalStorageService);
  private http = inject(HttpClient);

  private url = environment.APP_USERROLE_SERVICE_BASE_URL + '/api/v1/users';
  private _userConfig!: UserConfiguration;

  public get userTimeZone(): string {
    return this._userConfig.timezoneConfiguration.timezone;
  }

  public loadUserConfiguration(token: string): Promise<UserConfiguration> {
    const userRoleServiceUrl = AuthRealmService.prepareUrl(environment.APP_USERROLE_SERVICE_BASE_URL);
    const url = userRoleServiceUrl + '/api/v1/users/user-configuration';

    return fetch(url, {
      headers: {
        Accept: 'application/json',
        Authorization: 'Bearer ' + token,
        'X-Realm': AuthRealmService.getRealm(),
      },
    })
      .then((res) => {
        if (res.status >= 200 && res.status < 300) {
          return res.json();
        }
        console.error('Error in loadUserConfiguration', res.statusText);
        throw new Error(res.statusText);
      })
      .then((userConfig: UserConfiguration) => {
        this._userConfig = userConfig;
        return Promise.resolve(userConfig);
      })
      .catch((error) => {
        console.error('Error in loadUserConfiguration', error);
        throw new Error(error);
      });
  }

  public getSetting(setting: string) {
    return this.userLocalStorageService.getUserValue(setting);
  }

  public setSetting(setting: string, value: string) {
    this.userLocalStorageService.setUserValue(setting, value);
    return true; // @todo check for errors and return false
  }

  public removeSetting(setting: string) {
    this.userLocalStorageService.removeUserValue(setting);
    return true; // @todo check for errors and return false
  }

  public getDashboardConfiguration(): WidgetDefinition[] {
    return this._userConfig?.dashboardConfiguration?.dashboard ?? [];
  }

  public getEquipmentListConfiguration(): EquipmentListConfiguration {
    return this._userConfig.equipmentListConfiguration;
  }

  public getMapConfigurationEquipment(): MapConfiguration {
    return this._userConfig.equipmentMapConfiguration;
  }

  public getMapConfigurationMaintenance(): MapConfiguration {
    return this._userConfig.maintenanceMapConfiguration;
  }

  public getContactListConfiguration(): ContactListConfiguration {
    return this._userConfig.contactListConfiguration;
  }

  public getEmployeeListConfiguration(): EmployeeListConfiguration {
    return this._userConfig.employeeListConfiguration;
  }

  public getTransferLogListConfiguration(): TransferLogConfiguration {
    return this._userConfig.globalTransferLogListConfiguration;
  }

  public getMaintenanceListConfiguration(): MaintenanceListConfiguration {
    return this._userConfig.maintenanceListConfiguration;
  }

  public getMaintenanceCompletedListConfiguration(): MaintenanceListConfiguration {
    return this._userConfig.completedMaintenanceListConfiguration;
  }

  public getMaintenanceConfigListConfiguration(): MaintenanceConfigListConfiguration {
    return this._userConfig.maintenanceConfigurationListConfiguration;
  }

  public getAutomatedTransfersOnTheRoadListConfiguration(): AutomatedTransfersOnTheRoadListConfiguration {
    return this._userConfig.equipmentOnTheRoadListConfiguration;
  }

  public getAutomatedTransfersScheduledListConfiguration(): AutomatedTransfersScheduledListConfiguration {
    return this._userConfig.equipmentTransferScheduledListConfiguration;
  }

  public getAlarmSettingsListConfiguration(): AutomatedTransfersScheduledListConfiguration {
    return this._userConfig.alarmTypeConfigListConfiguration;
  }

  public getCustomerName(): string {
    return this._userConfig.customerName;
  }

  public getCustomerId(): string {
    return this._userConfig.customerId;
  }

  public getUsersByCustomer(customerId: string): Observable<ViewUser[]> {
    return this.http.get<ViewUser[]>(`${this.url}/by-customer/${customerId}`);
  }

  public getUserLanguage(): Languages {
    return this._userConfig.languageConfiguration?.language ?? '';
  }

  public getFallbackLanguage(): Languages {
    return this._userConfig?.additionalInfo?.fallbackLanguage || this.getDefaultLanguage();
  }

  public getDefaultLanguage(): Languages {
    return Languages.DEFAULT;
  }

  public saveDashboardConfiguration(configs: WidgetDefinition[]): Observable<string> {
    this._userConfig.dashboardConfiguration.dashboard = [...configs];
    return this.http.put(`${this.url}/save-dashboard-config`, JSON.stringify({ widgets: configs }), {
      responseType: 'text',
    });
  }

  public saveEquipmentListConfiguration(configs: EquipmentListConfiguration): Observable<string> {
    this._userConfig.equipmentListConfiguration = { ...configs };
    return this.http.put(`${this.url}/save-equipment-list-config`, configs, { responseType: 'text' });
  }

  public saveMapConfigurationEquipment(configs: MapConfiguration): Observable<string> {
    this._userConfig.equipmentMapConfiguration = { ...configs };
    return this.http.put(`${this.url}/save-equipment-map-config`, configs, { responseType: 'text' });
  }

  public saveMapConfigurationMaintenance(configs: MapConfiguration): Observable<string> {
    this._userConfig.maintenanceMapConfiguration = { ...configs };
    return this.http.put(`${this.url}/save-maintenance-map-config`, configs, { responseType: 'text' });
  }

  public saveContactListConfiguration(configs: ContactListConfiguration): Observable<string> {
    this._userConfig.contactListConfiguration = { ...configs };
    return this.http.put(`${this.url}/save-contact-list-config`, configs, { responseType: 'text' });
  }

  public saveEmployeeListConfiguration(configs: EmployeeListConfiguration): Observable<string> {
    this._userConfig.employeeListConfiguration = { ...configs };
    return this.http.put(`${this.url}/save-employee-list-config`, configs, { responseType: 'text' });
  }

  public saveTransferLogListConfiguration(configs: TransferLogConfiguration): Observable<string> {
    this._userConfig.globalTransferLogListConfiguration = { ...configs };
    return this.http.put(`${this.url}/save-global-transfer-log-list-config`, configs, { responseType: 'text' });
  }

  public saveMaintenanceListConfiguration(configs: MaintenanceListConfiguration): Observable<string> {
    this._userConfig.maintenanceListConfiguration = { ...configs };
    return this.http.put(`${this.url}/save-maintenance-list-config`, configs, { responseType: 'text' });
  }

  public saveCompletedMaintenanceListConfiguration(configs: MaintenanceListConfiguration): Observable<string> {
    this._userConfig.completedMaintenanceListConfiguration = { ...configs };
    return this.http.put(`${this.url}/save-completed-maintenance-list-config`, configs, { responseType: 'text' });
  }

  public saveMaintenanceConfigListConfiguration(configs: MaintenanceConfigListConfiguration): Observable<string> {
    this._userConfig.maintenanceConfigurationListConfiguration = { ...configs };
    return this.http.put(`${this.url}/save-maintenance-configuration-list-config`, configs, { responseType: 'text' });
  }

  public saveOnTheRoadListConfiguration(configs: AutomatedTransfersOnTheRoadListConfiguration): Observable<string> {
    this._userConfig.equipmentOnTheRoadListConfiguration = { ...configs };
    return this.http.put(`${this.url}/save-equipment-on-the-road-list-config`, configs, { responseType: 'text' });
  }

  public saveScheduledListConfiguration(configs: AutomatedTransfersScheduledListConfiguration): Observable<string> {
    this._userConfig.equipmentTransferScheduledListConfiguration = { ...configs };
    return this.http.put(`${this.url}/save-scheduled-transfer-list-config`, configs, { responseType: 'text' });
  }

  public saveEquipmentSearchV2Configuration(equipmentSearchConfiguration: EquipmentSearchConfiguration): void {
    if (!environment.EQUIPMENT_SEARCH_V2_TOGGLE_AVAILABLE) {
      return;
    }
    this._userConfig = { ...this._userConfig, equipmentSearchConfiguration };
  }

  public saveEquipmentEventsConfiguration(equipmentEventsConfiguration: EquipmentEventsConfiguration): void {
    if (!environment.EQUIPMENT_EVENTS_TOGGLE_AVAILABLE) {
      return;
    }
    this._userConfig = { ...this._userConfig, equipmentEventsConfiguration };
  }

  public saveAlarmSettingsConfiguration(configs: AlarmTypeConfigListConfiguration): Observable<string> {
    this._userConfig.alarmTypeConfigListConfiguration = { ...configs };
    return this.http.put(`${this.url}/save-alarm-settings-list-config`, configs, { responseType: 'text' });
  }

  public saveUserTimezone(timezone: string): Observable<unknown> {
    this._userConfig.timezoneConfiguration = { timezone, lastUpdated: new Date() };
    return this.http.put(`${this.url}/save-timezone-config`, { timezone }, { responseType: 'text' });
  }

  /* eslint-disable @typescript-eslint/no-explicit-any */
  public saveDimensionUnit(dimensionUnit: DimensionSystem): Observable<any> {
    const userConfiguration: UserConfiguration = this._userConfig;
    userConfiguration.dimensionUnitConfiguration.dimensionUnit = dimensionUnit;

    const command = new DimensionUnitConfiguration(dimensionUnit);
    return this.http.put(`${this.url}/save-dimension-unit-config`, command, { responseType: 'text' });
  }

  public getDimensionUnitConfiguration(): DimensionUnitConfiguration {
    return this._userConfig.dimensionUnitConfiguration;
  }

  public getUserDimensionSystem(): DimensionSystem {
    const dimensionUnitConfiguration = this.getDimensionUnitConfiguration();
    return dimensionUnitConfiguration.dimensionUnit ?? DimensionSystem.METRIC;
  }

  public getEquipmentSearchV2Configuration(): EquipmentSearchConfiguration {
    if (!environment.EQUIPMENT_SEARCH_V2_TOGGLE_AVAILABLE) {
      return { useSearchV2: false };
    }
    if (!this._userConfig?.equipmentSearchConfiguration) {
      this.saveEquipmentSearchV2Configuration({ useSearchV2: false });
    }
    return this._userConfig.equipmentSearchConfiguration;
  }

  public getEquipmentEventsConfiguration(): EquipmentEventsConfiguration {
    if (!environment.EQUIPMENT_EVENTS_TOGGLE_AVAILABLE) {
      return { eventsAvailable: false };
    }
    if (!this._userConfig?.equipmentEventsConfiguration) {
      this.saveEquipmentEventsConfiguration({ eventsAvailable: false });
    }
    return this._userConfig.equipmentEventsConfiguration;
  }

  public getUserTimezone(): string {
    if (!this._userConfig?.timezoneConfiguration?.lastUpdated) {
      this.applyAndSaveBrowserTimezone().subscribe();
    }
    return this._userConfig?.timezoneConfiguration?.timezone || '';
  }

  private applyAndSaveBrowserTimezone(): Observable<unknown> {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return this.saveUserTimezone(timezone);
  }

  public getDocumentsSortConfiguration(): DocumentsSortConfiguration {
    return this._userConfig.documentsSortConfiguration;
  }
}
